import { useMemo, useState, useEffect } from 'react';
import loadable from '@loadable/component';
import Statement from '@/components/Statement';
import Description from '@/components/Statement/components/Description';
import { Button, Row, Column, Alert, Text, Box } from '@simplywallst/ui-core';
import InformationSolid from '@simplywallst/ui-core/icons/InformationSolid';
import ReportSection from '@/components/ReportSection';
import ReportSubSection, {
  SubSectionTitle,
} from '@/components/ReportSubSection';
import track, { useTracking } from 'react-tracking';
import {
  ChartType,
  COMPANY_PAST_PERFORMANCE,
} from '@/constants/downloadSections';
import { getCompanyInfographicDownloadUrl } from '@/utilities/urlGenerator';
import {
  LegacyAnchor,
  PAST_EARNINGS_GROWTH,
  PAST_EARNINGS_REVENUE,
  PAST_ROA,
  PAST_ROE,
} from '@/constants/anchors';
import { CompanyStatements } from '@/constants/statementIds';
import PageAnchor from '@/components/PageAnchor';
import { BrazeContentCardWrapper } from '@/pages/CompanyReport/components/BrazeContentCard';
import CompanySectionTitle from '@/pages/CompanyReport/components/CompanySectionTitle';
import ChartWrapper from '@/pages/CompanyReport/components/Overview/components/ChartWrapper';
import { useVisibleSectionsIsPristine } from '@/pages/CompanyReport/components/VisibleSectionsContextProvider';
import useCompanyStatementsAPIFormatted from '@/hooks/useCompanyStatementsAPIFormatted';
import type { StatementName } from '@/hooks/useCompanyStatementsAPIFormatted/types';
import { useCompanyCanonicalUrl } from '@/hooks/useCompany';
import { getStatementChecks } from '@/pages/CompanyReport/components/Introduction/utils';
import { EarningsAndRevenueHistoryTable } from '../EarningsAndRevenueHistory/components/EarningsAndRevenueHistoryTable/EarningsAndRevenueHistoryTable';
import { useCompany } from '@/hooks/useCompany';
import FreeCashFlow from '../FreeCashFlow';
import {
  PastTranslation,
  usePastTranslation,
} from '../../hooks/usePastTranslation';

const PastHeader = loadable(() => import('../PastHeader'), {
  resolveComponent: (c) => c.PastHeader,
});

const BrazeContentCard = loadable(
  () => import('@/components/BrazeContentCard')
);

const ExtendedIncomeStatement = loadable(
  () => import('../ExtendedIncomeStatement')
);

const ROCEGauge = loadable(() => import('../ROCEGauge'), {
  ssr: false,
  resolveComponent: (c) => c.ROCEGauge,
});
const ROAGauge = loadable(() => import('../ROAGauge'), {
  ssr: false,
  resolveComponent: (c) => c.ROAGauge,
});
const ROEGauge = loadable(() => import('../ROEGauge'), {
  ssr: false,
  resolveComponent: (c) => c.ROEGauge,
});
const PastGrowth = loadable(
  () => import('@/pages/CompanyReport/components/Past'),
  {
    ssr: false,
    resolveComponent: (component) => component.PastGrowth,
  }
);

const EarningsAndRevenueHistory = loadable(
  () => import('../EarningsAndRevenueHistory'),
  { ssr: false }
);

const NextStep = loadable(
  () => import('@/pages/CompanyReport/components/SimilarCompanies/NextStep')
);

export interface PastProps {
  companyId: string;
  isFund: boolean;
  keyMetric: string;
  keyMetricTitle: string;
  pastROE: number;
  pastROA: number;
  pastROCE1y: number;
  pastROCE3y: number;
  netIncomeGrowth1y: number | null;
  companyName: string;
  sectionNumber?: number;
  netIncomeGrowth5y: number | null;
  pastIndustryROE: number;
  pastIndustryROA: number;
  marketName: string;
  industryName: string;
  averageMarketGrowth1Y: number;
  averageMarketGrowth5Y: number;
  averageIndustryGrowth1Y: number;
  averageIndustryGrowth5Y: number;
  netIncomeGrowth5YFormatted: string;
  averageIndustryGrowth5YFormatted: string;
  averageMarketGrowth5YFormatted: string;
  netIncomeGrowth1YFormatted: string;
  averageIndustryGrowth1YFormatted: string;
  averageMarketGrowth1YFormatted: string;
}

const Past = ({
  netIncomeGrowth1y,
  netIncomeGrowth5y,
  averageIndustryGrowth1Y,
  averageIndustryGrowth5Y,
  averageMarketGrowth1Y,
  averageMarketGrowth5Y,
  companyId,
  companyName,
  industryName,
  isFund,
  marketName,
  pastIndustryROA,
  pastIndustryROE,
  pastROA,
  pastROCE1y,
  pastROCE3y,
  pastROE,
  sectionNumber,
  netIncomeGrowth5YFormatted,
  averageIndustryGrowth5YFormatted,
  averageMarketGrowth5YFormatted,
  netIncomeGrowth1YFormatted,
  averageIndustryGrowth1YFormatted,
  averageMarketGrowth1YFormatted,
}: PastProps) => {
  const isPristine = useVisibleSectionsIsPristine();
  const subSectionTitleElement = isPristine ? 'h2' : 'h3';
  const [showReport, setShowReport] = useState(true);
  const tracking = useTracking();
  const trackingButtonData = {
    action: 'click',
    modifier: 'show_Meaningless_data',
  };

  const canonicalUrl = useCompanyCanonicalUrl();
  const { data: company } = useCompany(canonicalUrl);
  const isFilingProcessed =
    company?.data.analysis?.data.extended.data.analysis.past
      .latest_filing_processed;
  const { data: statements } = useCompanyStatementsAPIFormatted({
    canonicalURL: canonicalUrl,
  });

  const {
    HasHighQualityPastEarnings: hasHighQualityPastEarnings,
    HasPastNetProfitMarginImprovedOverLastYear:
      hasPastNetProfitMarginImprovedOverLastYear,
    HasGrownProfitsOverPast5Years: hasGrownProfitsOverPast5Years,
    HasProfitGrowthAccelerated: hasProfitGrowthAccelerated,
    IsGrowingFasterThanIndustry: isGrowingFasterThanIndustry,
    IsReturnOnEquityAboveThreshold: isReturnOnEquityAboveThreshold,
    IsPastAnalysisMeaningless: isPastAnalysisMeaningless,
  } = statements || {};

  const pastStatements: StatementName[] = [
    'HasHighQualityPastEarnings',
    'HasPastNetProfitMarginImprovedOverLastYear',
    'HasGrownProfitsOverPast5Years',
    'HasProfitGrowthAccelerated',
    'IsGrowingFasterThanIndustry',
    'IsReturnOnEquityAboveThreshold',
  ];
  const statementChecks = getStatementChecks(pastStatements, statements);

  const isDataMeaningless = isPastAnalysisMeaningless?.value === true;

  useEffect(() => {
    setShowReport(isDataMeaningless === false);
  }, [isDataMeaningless]);

  const {
    pastEarningsGrowthDownloadUrl,
    incomeStatementDownloadUrl,
    ROEDownloadUrl,
    ROADownloadUrl,
    ROCEDownloadUrl,
  } = useMemo(() => {
    return {
      pastEarningsGrowthDownloadUrl: getCompanyInfographicDownloadUrl(
        companyId,
        ChartType.PastEarningsGrowth
      ),
      incomeStatementDownloadUrl: getCompanyInfographicDownloadUrl(
        companyId,
        ChartType.EarningsAndRevenueHistory
      ),
      ROEDownloadUrl: getCompanyInfographicDownloadUrl(
        companyId,
        ChartType.ROE
      ),
      ROADownloadUrl: getCompanyInfographicDownloadUrl(
        companyId,
        COMPANY_PAST_PERFORMANCE
      ),
      ROCEDownloadUrl: getCompanyInfographicDownloadUrl(
        companyId,
        ChartType.ROCE
      ),
    };
  }, [companyId]);

  const t = usePastTranslation();

  return (
    <ReportSection sectionNumber={sectionNumber} data-cy-id="past-section">
      <CompanySectionTitle title={t('heading')} />
      {isFilingProcessed === false && (
        <Box marginY="x2">
          <Alert color="phantom" icon={<InformationSolid fill="white" />}>
            <Text typography="xSmall">{t('noFilingProcessed')}</Text>
          </Alert>
        </Box>
      )}
      <PastHeader
        statementChecks={statementChecks}
        canonicalUrl={canonicalUrl}
        industryName={industryName}
      />
      {isDataMeaningless && (
        <ReportSubSection
          body={
            <PastTranslation
              i18nKey="meaninglessData"
              values={{ company: companyName }}
            >
              <p>
                In this section we usually display a company’s past earnings and
                revenues to help investors visualise the trend through time. We
                also gauge the company’s performance by benchmarking its returns
                and growth to its industry peers and the wider market. However,
                {companyName} has not provided sufficient past data to assess
                its track record.
              </p>
              <p>
                This is quite a rare situation as 97% of companies covered by
                Simply Wall St do have historical data.
              </p>
              {showReport === false && (
                <Button
                  styleType="louderLarge"
                  onClick={() => {
                    tracking.trackEvent(trackingButtonData);
                    setShowReport(!showReport);
                  }}
                >
                  Show section
                </Button>
              )}
            </PastTranslation>
          }
        />
      )}

      {showReport && (
        <>
          <BrazeContentCardWrapper>
            <BrazeContentCard
              feedType="company-overview.past_performance"
              anchorTarget="_blank"
            />
          </BrazeContentCardWrapper>
          <PageAnchor type="sub-section-title" id={PAST_EARNINGS_REVENUE} />
          <ExtendedIncomeStatement companyName={companyName} />
          <ReportSubSection
            titleElement={subSectionTitleElement}
            title={t('earningsHistory.heading')}
            trackingProps={{ subject: 'chart' }}
            bodyRender={() => (
              <ChartWrapper
                id="past-earnings-revenue-history-chart"
                chart={
                  <EarningsAndRevenueHistory
                    companyId={companyId}
                    labels={{
                      seriesRevenue: t('earningsHistory.chart.revenue'),
                      seriesEarnings: t('earningsHistory.chart.earnings'),
                      seriesFreeCashFlow: t(
                        'earningsHistory.chart.freeCashFlow'
                      ),
                      seriesCashFromOp: t('earningsHistory.chart.cashFromOp'),
                      seriesOperatingExpenses: t(
                        'earningsHistory.chart.operating'
                      ),
                      valuePerYearPostfix: t('earningsHistory.chart.perYear'),
                      valueProfitMarginPostfix: t(
                        'earningsHistory.chart.profitMargin'
                      ),
                    }}
                  />
                }
                table={<EarningsAndRevenueHistoryTable />}
                feedbackLabel={t('earningsHistory.heading')}
                exportDarkUrl={incomeStatementDownloadUrl}
                preRenderMinHeightSmDown="478px"
                preRenderHeight="502px"
              />
            )}
            statements={
              <>
                <Statement
                  title={hasHighQualityPastEarnings?.title}
                  description={
                    <Description statement={hasHighQualityPastEarnings} />
                  }
                  value={hasHighQualityPastEarnings?.value}
                  statementId={CompanyStatements.pastStatement5}
                />
                <Statement
                  title={hasPastNetProfitMarginImprovedOverLastYear?.title}
                  description={
                    <Description
                      statement={hasPastNetProfitMarginImprovedOverLastYear}
                    />
                  }
                  value={hasPastNetProfitMarginImprovedOverLastYear?.value}
                  statementId={CompanyStatements.pastStatement6}
                />
              </>
            }
          />
          <hr />
          <FreeCashFlow />
          <hr />
          <PageAnchor type="sub-section-title" id={PAST_EARNINGS_GROWTH} />
          <ReportSubSection
            titleElement={subSectionTitleElement}
            title={t('pastEarnings.heading')}
            trackingProps={{ subject: 'chart' }}
            body={
              <ChartWrapper
                id="past-earnings-growth-analysis-chart"
                preRenderHeight="305px"
                preRenderMinHeightSmDown="600px"
                chart={
                  <PastGrowth
                    companyName={companyName}
                    industryName={industryName}
                    marketName={marketName}
                    netIncomeGrowth5Y={netIncomeGrowth5y}
                    netIncomeGrowth1Y={netIncomeGrowth1y}
                    averageMarketGrowth1Y={averageMarketGrowth1Y}
                    averageMarketGrowth5Y={averageMarketGrowth5Y}
                    averageIndustryGrowth1Y={averageIndustryGrowth1Y}
                    averageIndustryGrowth5Y={averageIndustryGrowth5Y}
                    netIncomeGrowth5YFormatted={netIncomeGrowth5YFormatted}
                    averageIndustryGrowth5YFormatted={
                      averageIndustryGrowth5YFormatted
                    }
                    averageMarketGrowth5YFormatted={
                      averageMarketGrowth5YFormatted
                    }
                    netIncomeGrowth1YFormatted={netIncomeGrowth1YFormatted}
                    averageIndustryGrowth1YFormatted={
                      averageIndustryGrowth1YFormatted
                    }
                    averageMarketGrowth1YFormatted={
                      averageMarketGrowth1YFormatted
                    }
                    labels={{
                      chartPast5Years: t('pastEarnings.chart.past5years'),
                      chartPast1Year: t('pastEarnings.chart.past1year'),
                      keyCompany: t('pastEarnings.chart.company'),
                      keyIndustry: t('pastEarnings.chart.industry'),
                      keyMarket: t('pastEarnings.chart.market'),
                    }}
                  />
                }
                feedbackLabel={t('pastEarnings.chart.feedback')}
                exportDarkUrl={pastEarningsGrowthDownloadUrl}
              />
            }
            statements={
              <>
                <Statement
                  title={hasGrownProfitsOverPast5Years?.title}
                  description={
                    <Description statement={hasGrownProfitsOverPast5Years} />
                  }
                  value={hasGrownProfitsOverPast5Years?.value}
                  statementId={CompanyStatements.pastStatement1}
                />
                <Statement
                  title={hasProfitGrowthAccelerated?.title}
                  description={
                    <Description statement={hasProfitGrowthAccelerated} />
                  }
                  value={hasProfitGrowthAccelerated?.value}
                  statementId={CompanyStatements.pastStatement2}
                />
                <Statement
                  title={isGrowingFasterThanIndustry?.title}
                  description={
                    <Description statement={isGrowingFasterThanIndustry} />
                  }
                  value={isGrowingFasterThanIndustry?.value}
                  statementId={CompanyStatements.pastStatement3}
                />
              </>
            }
          />
          <hr />
          <PageAnchor type="sub-section-title" id={PAST_ROE} />
          <PageAnchor type="sub-section-title" id={LegacyAnchor.LAST_PERF} />
          <ReportSubSection
            titleElement={subSectionTitleElement}
            title={t('returnOnEquity.heading')}
            trackingProps={{ subject: 'chart' }}
            split={true}
            body={
              <ChartWrapper
                id="past-return-on-equity-chart"
                preRenderHeight="280px"
                preRenderMinHeightMdDown="275px"
                chart={
                  <ROEGauge
                    companyROE={pastROE}
                    companyName={companyName}
                    industryROE={pastIndustryROE}
                    industryName={industryName}
                    labels={{
                      title: t('returnOnEquity.chart.title'),
                      company: t('returnOnEquity.chart.company'),
                      industry: t('returnOnEquity.chart.industry'),
                      notAvailable: t('returnOnEquity.chart.notAvailable'),
                    }}
                  />
                }
                feedbackLabel={t('returnOnEquity.feedback')}
                exportDarkUrl={ROEDownloadUrl}
              />
            }
            statements={
              <Statement
                title={isReturnOnEquityAboveThreshold?.title}
                description={
                  <Description statement={isReturnOnEquityAboveThreshold} />
                }
                value={isReturnOnEquityAboveThreshold?.value}
                statementId={CompanyStatements.pastStatement4}
              />
            }
          />
          <PageAnchor type="sub-section-title" id={PAST_ROA} />
          <PageAnchor type="sub-section-title" id={LegacyAnchor.PAST_ROCE} />
          <ReportSubSection
            trackingProps={{ subject: 'chart' }}
            body={
              <Row>
                <Column col={12} colLg={6}>
                  <hr />
                  <SubSectionTitle
                    as={subSectionTitleElement}
                    data-cy-id="report-sub-section-title-return-on-assets"
                  >
                    {t('returnOnAssets.heading')}
                  </SubSectionTitle>
                  <ChartWrapper
                    id="past-return-on-assets-chart"
                    preRenderHeight="280px"
                    preRenderMinHeightMdDown="275px"
                    chart={
                      <ROAGauge
                        companyROA={pastROA}
                        companyName={companyName}
                        industryROA={pastIndustryROA}
                        industryName={industryName}
                        labels={{
                          title: t('returnOnAssets.chart.title'),
                          company: t('returnOnAssets.chart.company'),
                          industry: t('returnOnAssets.chart.industry'),
                          notAvailable: t('returnOnAssets.chart.notAvailable'),
                        }}
                      />
                    }
                    feedbackLabel={t('returnOnAssets.feedback')}
                    exportDarkUrl={ROADownloadUrl}
                  />
                </Column>
                <Column col={12} colLg={6}>
                  <hr />
                  <SubSectionTitle
                    as={subSectionTitleElement}
                    data-cy-id="report-sub-section-title-return-on-capital-employed"
                  >
                    {t('returnOnCapital.heading')}
                  </SubSectionTitle>
                  <ChartWrapper
                    id="past-return-capital-employed-chart"
                    preRenderHeight="280px"
                    preRenderMinHeightMdDown="275px"
                    chart={
                      <ROCEGauge
                        companyName={companyName}
                        ROCE1y={pastROCE1y}
                        ROCE3y={pastROCE3y}
                        labels={{
                          title: t('returnOnCapital.chart.title'),
                          titleROCE1y: t('returnOnCapital.chart.titleROCE1y', {
                            company: companyName,
                          }),
                          titleROCE3y: t('returnOnCapital.chart.titleROCE3y', {
                            company: companyName,
                          }),
                          keyROCE1y: t('returnOnCapital.chart.ROCE1y'),
                          keyROCE3y: t('returnOnCapital.chart.ROCE3y'),
                          notAvailable: t('returnOnCapital.chart.notAvailable'),
                        }}
                      />
                    }
                    feedbackLabel={t('returnOnCapital.feedback')}
                    exportDarkUrl={ROCEDownloadUrl}
                  />
                </Column>
              </Row>
            }
          />
        </>
      )}
      {isFund === false && (
        <>
          <hr />
          <NextStep section="past" title={t('nextStep.title')} />
        </>
      )}
    </ReportSection>
  );
};

export default track({ type: 'track', subject: 'Past' })(Past);
